import { PixelBuffer } from "../pixel/PixelBuffer";
import type { Selection } from "../selection/Selection";
import type { Filter } from "./Filter";

export type PixelateMode = "mosaic" | "color-halftone";

export interface PixelateParams {
  mode: PixelateMode;
  cellSize: number;
  maxRadius: number;
  screenAngles: [number, number, number, number];
}

// ---------------------------------------------------------------------------
// Mosaic (pixelate)
// ---------------------------------------------------------------------------

function applyMosaic(
  src: Uint8ClampedArray,
  dst: Uint8ClampedArray,
  w: number,
  h: number,
  cellSize: number,
): void {
  for (let cy = 0; cy < h; cy += cellSize) {
    for (let cx = 0; cx < w; cx += cellSize) {
      let sumR = 0;
      let sumG = 0;
      let sumB = 0;
      let count = 0;
      const endY = Math.min(cy + cellSize, h);
      const endX = Math.min(cx + cellSize, w);
      for (let y = cy; y < endY; y++) {
        for (let x = cx; x < endX; x++) {
          const o = (y * w + x) * 4;
          sumR += src[o]!;
          sumG += src[o + 1]!;
          sumB += src[o + 2]!;
          count++;
        }
      }
      const avgR = Math.round(sumR / count);
      const avgG = Math.round(sumG / count);
      const avgB = Math.round(sumB / count);
      for (let y = cy; y < endY; y++) {
        for (let x = cx; x < endX; x++) {
          const o = (y * w + x) * 4;
          dst[o] = avgR;
          dst[o + 1] = avgG;
          dst[o + 2] = avgB;
          dst[o + 3] = src[o + 3]!;
        }
      }
    }
  }
}

// ---------------------------------------------------------------------------
// Color Halftone
// ---------------------------------------------------------------------------

function applyColorHalftone(
  src: Uint8ClampedArray,
  dst: Uint8ClampedArray,
  w: number,
  h: number,
  maxRadius: number,
  screenAngles: [number, number, number, number],
): void {
  const n = w * h;
  const rad = screenAngles.map((a) => (a * Math.PI) / 180);
  const cosA = rad.map((a) => Math.cos(a));
  const sinA = rad.map((a) => Math.sin(a));

  for (let i = 0; i < n; i++) {
    const y = Math.floor(i / w);
    const x = i % w;
    const o = i * 4;

    const r = src[o]!;
    const g = src[o + 1]!;
    const b = src[o + 2]!;

    // RGB -> CMYK (all in 0..255)
    const rf = r / 255;
    const gf = g / 255;
    const bf = b / 255;
    const k = 1 - Math.max(rf, gf, bf);
    const c = k < 1 ? (1 - rf - k) / (1 - k) : 0;
    const m = k < 1 ? (1 - gf - k) / (1 - k) : 0;
    const yc = k < 1 ? (1 - bf - k) / (1 - k) : 0;
    const cmyk = [c * 255, m * 255, yc * 255, k * 255];

    const halftone = [0, 0, 0, 0];
    for (let ch = 0; ch < 4; ch++) {
      const rx = x * cosA[ch]! + y * sinA[ch]!;
      const pattern = (Math.sin(rx * Math.PI / maxRadius) + 1) / 2;
      halftone[ch] = cmyk[ch]! / 255 > pattern ? 0 : 255;
    }

    // CMYK -> RGB
    const cf = halftone[0]! / 255;
    const mf = halftone[1]! / 255;
    const yf = halftone[2]! / 255;
    const kf = halftone[3]! / 255;

    dst[o] = Math.round(255 * (1 - cf) * (1 - kf));
    dst[o + 1] = Math.round(255 * (1 - mf) * (1 - kf));
    dst[o + 2] = Math.round(255 * (1 - yf) * (1 - kf));
    dst[o + 3] = src[o + 3]!;
  }
}

// ---------------------------------------------------------------------------
// Filter export
// ---------------------------------------------------------------------------

export const PixelateSuite: Filter<PixelateParams> = {
  id: "pixelate-suite",
  displayName: "Pixelate",
  defaultParams: {
    mode: "mosaic",
    cellSize: 8,
    maxRadius: 8,
    screenAngles: [0, 15, 30, 45],
  },

  apply(
    input: PixelBuffer,
    params: PixelateParams,
    _selection: Selection | null,
  ): PixelBuffer {
    const w = input.width;
    const h = input.height;
    const out = new PixelBuffer(w, h, input.mode);
    const src = input.data;
    const dst = out.data;

    if (w === 0 || h === 0) {
      dst.set(src);
      return out;
    }

    switch (params.mode) {
      case "mosaic":
        applyMosaic(src, dst, w, h, params.cellSize);
        break;
      case "color-halftone":
        applyColorHalftone(
          src,
          dst,
          w,
          h,
          params.maxRadius,
          params.screenAngles,
        );
        break;
    }

    return out;
  },
};
